import { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { api } from '../../../services/api'

function ProductSelector({ selectedIds = [], onChange, maxProducts = 10 }) {
  const { t } = useTranslation('ecommerce')
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.getProducts()
        setProducts(response.products || [])
      } catch (error) {
        console.error('Failed to fetch products:', error)
        setProducts([])
      } finally {
        setLoading(false)
      }
    }
    fetchProducts()
  }, [])

  const handleToggle = useCallback((productId) => {
    const isSelected = selectedIds.includes(productId)
    if (isSelected) {
      onChange(selectedIds.filter((id) => id !== productId))
    } else {
      onChange([...selectedIds, productId])
    }
  }, [selectedIds, onChange])

  const isMaxReached = selectedIds.length >= maxProducts

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="text-gray-500">{t('bioPage.products.loading', 'Loading...')}</span>
      </div>
    )
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {t('bioPage.products.noProducts', 'No products available')}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>{t('bioPage.products.selectProducts', 'Select products')}</span>
        <span>
          {selectedIds.length} {t('bioPage.products.of', 'of')} {maxProducts}
        </span>
      </div>

      <ul className="space-y-2" role="list">
        {products.map((product) => {
          const isSelected = selectedIds.includes(product.product_id)
          const isDisabled = !isSelected && isMaxReached

          return (
            <li
              key={product.product_id}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                isSelected
                  ? 'border-primary-500 bg-primary-50'
                  : isDisabled
                    ? 'border-gray-100 bg-gray-50 opacity-50'
                    : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="checkbox"
                id={`product-${product.product_id}`}
                checked={isSelected}
                disabled={isDisabled}
                onChange={() => handleToggle(product.product_id)}
                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                aria-label={product.title}
              />
              {product.image_url && (
                <img
                  src={product.image_url}
                  alt={product.title}
                  className="w-12 h-12 object-cover rounded"
                />
              )}
              <div className="flex-1 min-w-0">
                <label
                  htmlFor={`product-${product.product_id}`}
                  className="block text-sm font-medium text-gray-900 truncate cursor-pointer"
                >
                  {product.title}
                </label>
                <span className="text-sm text-gray-500">
                  €{product.price?.toFixed(2)}
                </span>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

ProductSelector.propTypes = {
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  maxProducts: PropTypes.number,
}

export default ProductSelector
